import { useMcpTokens } from '@/lib/mcp-tokens-queries';
import type { CreatedMcpToken, McpEnvironment, McpToken } from '@mss_creator/shared';
import { Alert, Badge, Button, EmptyCard } from '@pms-digital/design-system';
import { useRef, useState } from 'react';
import { ceilingMessage } from './mcp-ceiling';
import { McpTokenCreateDialog } from './mcp-token-create-dialog';
import { McpTokenRevealDialog } from './mcp-token-reveal-dialog';
import { McpTokenRevokeDialog } from './mcp-token-revoke-dialog';
import { McpTokenTable } from './mcp-token-table';
import { SettingRow } from './setting-row';
import { SettingSection } from './setting-section';

const DESCRIPTIONS: Record<McpEnvironment, string> = {
  dev: "Sur dev, un agent passe aussi par Authentik : il lui faut votre mot de passe d'application Authentik en plus du jeton. Les écritures passent toujours par une prévisualisation. Révoquez un jeton dès qu'il ne sert plus.",
  prod: 'Un agent peut lire toutes les données de production avec ce jeton, jamais les modifier. Pour copier des données vers dev, passez par le MCP de dev.',
};

const EMPTY_MESSAGES: Record<McpEnvironment, string> = {
  dev: "Aucun jeton MCP. Créez un jeton pour qu'un agent lise et modifie les données de dev.",
  prod: "Aucun jeton MCP. Créez un jeton pour qu'un agent lise les données de production.",
};

function TokensSkeleton() {
  return (
    <output className="block" aria-label="Chargement des jetons MCP">
      <div className="flex flex-col gap-2 border-gray-200 border-t py-4" aria-hidden="true">
        <span className="block h-3.5 w-56 rounded-sm bg-gray-100" />
        <span className="block h-3 w-full max-w-[520px] rounded-sm bg-gray-100" />
        <span className="mt-1 block h-14 w-full max-w-[560px] rounded-md bg-gray-100" />
      </div>
    </output>
  );
}

function activeLabel(count: number, max: number): string {
  return `${count} ${count > 1 ? 'jetons actifs' : 'jeton actif'} sur ${max}.`;
}

/**
 * « Accès MCP » of the "Génération & système" tab, shown to a real `pms_admin`
 * only (the caller decides): the admin's personal tokens on this environment
 * (spec D21). Create (the value is shown once), list, revoke with a
 * confirmation. At the ceiling of active tokens the button keeps its focus but
 * does nothing, and the reason is written beside it.
 */
export function McpAccessSection() {
  const tokensQuery = useMcpTokens();
  const createButtonRef = useRef<HTMLButtonElement>(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedMcpToken | null>(null);
  const [revoking, setRevoking] = useState<McpToken | null>(null);

  const list = tokensQuery.data;
  const activeCount = list?.tokens.filter((token) => token.status === 'active').length ?? 0;
  const atCeiling = list !== undefined && activeCount >= list.maxActive;

  return (
    <SettingSection
      title="Accès MCP"
      where="Permet à un agent (Claude Code, par exemple) d'accéder aux données de cet environnement. Chaque jeton agit en votre nom."
    >
      {tokensQuery.isPending ? (
        <TokensSkeleton />
      ) : list === undefined ? (
        <div className="mt-3">
          <Alert
            tone="danger"
            title="Impossible de charger vos jetons MCP"
            action={
              <Button variant="primary" size="sm" onClick={() => void tokensQuery.refetch()}>
                Réessayer
              </Button>
            }
          >
            Vérifiez votre connexion, puis réessayez.
          </Alert>
        </div>
      ) : (
        <SettingRow
          label="Mes jetons"
          description={DESCRIPTIONS[list.environment]}
          status={
            <>
              <Badge tone="neutral" icon="person" maxWidth="none">
                Votre compte
              </Badge>
              <Badge
                tone="accent"
                icon={list.environment === 'dev' ? 'dns' : 'visibility'}
                maxWidth="none"
              >
                {list.environment === 'dev' ? 'Dev : lecture et écriture' : 'Prod : lecture seule'}
              </Badge>
            </>
          }
        >
          {list.tokens.length === 0 ? (
            <EmptyCard
              icon="key"
              message={EMPTY_MESSAGES[list.environment]}
              action={
                <Button
                  ref={createButtonRef}
                  variant="primary"
                  icon="add"
                  onClick={() => setCreating(true)}
                >
                  Créer un jeton
                </Button>
              }
            />
          ) : (
            <>
              <McpTokenTable tokens={list.tokens} onRevoke={setRevoking} />
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  ref={createButtonRef}
                  variant="primary"
                  icon="add"
                  aria-disabled={atCeiling}
                  aria-describedby="mcp-create-hint"
                  onClick={() => {
                    if (!atCeiling) setCreating(true);
                  }}
                >
                  Créer un jeton
                </Button>
                <span id="mcp-create-hint" className="text-gray-600 text-small">
                  {atCeiling
                    ? ceilingMessage(list.maxActive)
                    : activeLabel(activeCount, list.maxActive)}
                </span>
              </div>
            </>
          )}
          {creating && (
            <McpTokenCreateDialog
              maxActive={list.maxActive}
              onClose={() => setCreating(false)}
              onCreated={(value) => {
                setCreating(false);
                setCreated(value);
              }}
            />
          )}
          {created && (
            <McpTokenRevealDialog
              created={created}
              environment={list.environment}
              returnFocusTo={createButtonRef}
              onDone={() => setCreated(null)}
            />
          )}
          {revoking && <McpTokenRevokeDialog token={revoking} onClose={() => setRevoking(null)} />}
        </SettingRow>
      )}
    </SettingSection>
  );
}
